import * as Sentry from '@sentry/react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';

import App from './app';
import { getTenantRedirectUri } from './utils/multi-tenant-uri';
import AnalyticsRouteTracker from './utils/analytics-route-tracker';
import { isDev, getRuntimeConfig } from './utils/configUtil/configUtil';
import { UPDATE_PROGRESS_SPAN } from './utils/constants/progressConstants';

// vConsole 적용 (for application debug)
console.log('APP PHASE : ', import.meta.env.VITE_APP_PHASE);

if (import.meta.env.VITE_APP_PHASE !== 'PROD') {
  import('vconsole').then((vc) => {
    // eslint-disable-next-line new-cap
    const vconsole = new vc.default({ theme: 'dark' });

    return vconsole;
  });
}

// Sentry 환경 변수 파싱 (문자열 → 숫자 변환)
// 환경 변수는 문자열로 전달되므로 숫자로 변환 필요 (리플레이 저장 보장을 위해 필수)
const traceSampleRate = Number(import.meta.env.VITE_TRACE_SAMPLE_RATE) || 0.1;
const replaysSessionSampleRate = Number(import.meta.env.VITE_REPLAY_SESSION_SAMPLE_RATE) || 0.0;
const replaysOnErrorSampleRate = Number(import.meta.env.VITE_REPLAY_ERROR_SAMPLE_RATE) || 1.0;

// Sentry 리플레이 마스킹 설정
// 개발, 스테이지 환경에서는 리플레이 마스킹 비활성화
const { origin } = window.location;
const isStage = origin.includes('.stage.');
const maskReplayText = !isDev() && !isStage;
console.log('maskReplayText : ', maskReplayText);

// Sentry
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_APP_PHASE,
  tracesSampler(samplingContext) {
    if (samplingContext.name === UPDATE_PROGRESS_SPAN) {
      return 1;
    }

    return traceSampleRate;
  },
  integrations: [
    Sentry.replayIntegration({
      maskAllText: maskReplayText, // 모든 텍스트 마스킹
      blockAllMedia: true, // 미디어 파일 차단 (네트워크 트래픽 감소 및 민감한 데이터 캡쳐 차단)
    }),
  ],

  replaysSessionSampleRate, // 일반 세션을 기록할 비율
  replaysOnErrorSampleRate, // 에러가 발생한 세션을 기록할 비율 (1.0 = 100% 저장)
});

// baseName 자동 설정 함수
const getBaseName = () => {
  const brandCode = getTenantRedirectUri();
  const runtimeConfig = getRuntimeConfig();

  // 404 페이지 접근 시 루프 방지
  if (brandCode === '404') {
    console.log('Already at /404 — skipping basename redirect.');
    return '';
  }

  // 고객사코드가 없거나, runtimeConfig가 없다면 basename을 "/"로 설정
  if (!brandCode || !runtimeConfig) {
    return '';
  }

  // 스테이지/운영 환경에서 브랜드 경로가 없다면
  if (!isDev() && !brandCode) {
    window.location.href = '/404';
    return '';
  }

  // 스테이지, 운영은 첫번째 경로(/a, /b 등)를 베이스로 사용
  return `/${brandCode || ''}`;
};

// ----------------------------------------------------------------------

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

root.render(
  <HelmetProvider>
    <BrowserRouter basename={getBaseName()}>
      <AnalyticsRouteTracker />
      <App />
    </BrowserRouter>
  </HelmetProvider>
);
